import { AgentIconWithFallback } from 'ai-components-ui-library/components/icons/AgentIconWithFallback';
import { AuthorizationStatus, NotificationStatus } from 'connections-embed-lib';
import { FavoriteIcon } from 'foundations-assets/icons/components/FavoriteIcon';
import { Flex } from 'foundations-components/components/Flex';
import { Text } from 'foundations-components/components/Text';
import { View } from 'foundations-components/components/View';
import { Badge } from 'foundations-components/transitional/components/Badge';
import { CardWrapper } from 'foundations-components/transitional/components/Card';
import { IconHolder } from 'foundations-components/transitional/components/IconHolder';
import { Image } from 'foundations-components/transitional/components/Image';
import { Ribbon } from 'foundations-components/transitional/components/Ribbon';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { formatInstalls } from 'marketplace-ui-common/utils/installs';
import { MAX_VISIBLE_ICONS } from 'marketplace-ui-common/workflow-templates/constants/icon-group';
import { WorkflowTemplateIconGroup } from 'marketplace-ui-common/workflow-templates/WorkflowTemplateIconGroup';
import { BreezeBadge } from 'trellis-ecosystem';
import { renderCardButtons } from './internal/CardButtons';
import { useLazyImage } from './internal/LazyImageProvider';
import { MarketplaceCardBase } from './internal/MarketplaceCardBase';
import { MarketplaceCardClickable } from './internal/MarketplaceCardClickable';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const DISPLAYED_PRODUCT_FEATURES = ['MCP_SERVER'];
export function MarketplaceCardMedium({
  buttons,
  listing,
  onClick,
  tracker,
  trackingProps
}) {
  var _listing$reviewSummar, _listing$installCount;
  const product = listing.products[0];
  const cardButtons = renderCardButtons({
    buttons,
    listing
  });
  const hasCardButtons = !!cardButtons.length;
  const rating = (_listing$reviewSummar = listing.reviewSummary) === null || _listing$reviewSummar === void 0 ? void 0 : _listing$reviewSummar.overallRating;
  return /*#__PURE__*/_jsx(MarketplaceCardBase, {
    componentType: "MarketplaceCardMedium",
    listing: listing,
    tracker: tracker,
    trackingProps: trackingProps,
    children: /*#__PURE__*/_jsxs(CardWrapper, {
      marginBlockEnd: "0",
      blockSize: "100%"
      // For backwards compatibility. Check Sourcegraph before removing.
      ,
      "data-test-id": "ecosystem-app-card",
      children: [/*#__PURE__*/_jsxs(MarketplaceCardClickable, {
        blockSize: "100%",
        componentType: "MarketplaceCardMedium",
        display: "flex",
        flexDirection: "column",
        listing: listing,
        onClick: onClick,
        paddingBlockEnd: hasCardButtons ? undefined : '600',
        paddingBlockStart: "600",
        paddingInline: "600",
        trackingProps: trackingProps,
        children: [listing.betaStage && /*#__PURE__*/_jsx(Ribbon, {
          position: "left",
          use: "beta",
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "marketplace-embed.card.beta"
          })
        }), product.installed && product.productId && /*#__PURE__*/_jsx(NotificationStatus, {
          appId: product.productId,
          type: "ribbon"
        }), /*#__PURE__*/_jsx(View, {
          alignSelf: "start",
          children: /*#__PURE__*/_jsx(ListingIcon, {
            listing: listing
          })
        }), /*#__PURE__*/_jsx(Text, {
          as: "h4",
          typography: "heading-100",
          truncate: {
            lineClamp: 1
          },
          marginBlockStart: "400",
          children: listing.listingName
        }), /*#__PURE__*/_jsxs(Text, {
          as: "small",
          color: "text-core-subtle",
          marginBlockStart: "100",
          children: [/*#__PURE__*/_jsx(Text, {
            as: "span",
            marginInlineEnd: "400",
            children: /*#__PURE__*/_jsx(FormattedMessage, {
              message: "marketplace-embed.card.providerName",
              options: {
                providerName: listing.companyName
              }
            })
          }), /*#__PURE__*/_jsx(Text, {
            whiteSpace: "nowrap",
            as: "span",
            display: "inline-block",
            children: product.installed && product.productId ? /*#__PURE__*/_jsx(View, {
              marginBlock: "-200",
              children: /*#__PURE__*/_jsx(AuthorizationStatus, {
                appId: product.productId,
                showInstalledStatus: true
              })
            }) : formatInstalls((_listing$installCount = listing.installCount) !== null && _listing$installCount !== void 0 ? _listing$installCount : 0)
          })]
        }), /*#__PURE__*/_jsx(Text, {
          marginBlockStart: "200",
          truncate: {
            lineClamp: 2
          },
          children: listing.description
        }), !hasCardButtons && /*#__PURE__*/_jsxs(Flex, {
          alignItems: "center",
          flexWrap: "wrap",
          gap: "200",
          marginBlockStart: "auto",
          paddingBlockStart: "400",
          children: [/*#__PURE__*/_jsx(Badge, {
            use: "koala",
            children: listing.displayTagLabel
          }), listing.products.some(p => p.worksWithBreeze) && /*#__PURE__*/_jsx(BreezeBadge, {}), product.productFeatures.filter(pf => pf.type && DISPLAYED_PRODUCT_FEATURES.includes(pf.type)).map(pf => /*#__PURE__*/_jsx(Badge, {
            use: "koala",
            children: pf.name
          }, `${String(pf.type)}-${pf.name}`)), product.productType === 'SOLUTIONS_PARTNER_PROFILE' && rating !== undefined && /*#__PURE__*/_jsx(Badge, {
            use: "koala",
            display: "flex",
            flexDirection: "row",
            gap: "400",
            children: /*#__PURE__*/_jsxs(Flex, {
              gap: "100",
              alignItems: "center",
              children: [rating.toFixed(1), /*#__PURE__*/_jsx(IconHolder, {
                color: "icon-core-subtle",
                icon: FavoriteIcon,
                marginBlockEnd: "25",
                padding: "0"
              })]
            })
          })]
        })]
      }), hasCardButtons && /*#__PURE__*/_jsx(Flex, {
        marginBlockStart: "auto",
        paddingBlockEnd: "600",
        paddingBlockStart: "400",
        paddingInline: "600",
        children: cardButtons
      })]
    })
  });
}
function ListingIcon({
  listing
}) {
  const lazyImage = useLazyImage();
  const product = listing.products[0];
  const isWorkflowTemplate = (product === null || product === void 0 ? void 0 : product.productType) === 'WORKFLOW_TEMPLATE';
  const isPartner = (product === null || product === void 0 ? void 0 : product.productType) === 'SOLUTIONS_PARTNER_PROFILE';
  if (isWorkflowTemplate) {
    return /*#__PURE__*/_jsx(WorkflowTemplateIconGroup, {
      maxIcons: MAX_VISIBLE_ICONS,
      workflowActionTypes: product.builtInWorkflowActionTypes
    });
  }
  if (isPartner && listing.iconUrl) {
    return /*#__PURE__*/_jsx(Image, {
      alt: listing.listingName,
      blockSize: "48px",
      loading: lazyImage,
      marginBlockEnd: "0",
      maxInlineSize: "120px",
      minInlineSize: "48px",
      objectFit: "contain",
      responsive: false,
      shape: "rounded",
      src: listing.iconUrl
    });
  }
  const icon = /*#__PURE__*/_jsx(AgentIconWithFallback, {
    iconUrl: listing.iconUrl,
    loading: lazyImage,
    marginBlockEnd: "0",
    name: listing.listingName
    // accomodate the width of the border (if it exists)
    ,
    size: listing.iconUrl ? 48 : 46
  });
  return listing.iconUrl ? icon :
  /*#__PURE__*/
  // add a border because otherwise it clashes with the gray background and looks ugly
  _jsx(View, {
    borderColor: "border-accent-magenta-default",
    borderWidth: "100",
    borderRadius: "300",
    borderStyle: "solid",
    overflow: "hidden",
    children: icon
  });
}